import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import SendRounded from '@mui/icons-material/SendRounded';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useLiveAiVoice from '../../hooks/useLiveAiVoice';
import useViewportMode from '../../hooks/useViewportMode';
import LiveAiVoiceControls from './LiveAiVoiceControls';
import {
    ContextualAiAction,
    ContextualAiCoverageDecision,
    ContextualAiCoverageSummary,
    ContextualAiMessage,
    ContextualAiTurnResult,
    ContextualAiWorkflowAdapter,
} from './contextualAiTypes';

export interface ContextualAiWorkspaceProps<TResult = unknown> {
    adapter: ContextualAiWorkflowAdapter<TResult>;
    ariaLabel?: string;
    composerLabel?: string;
    emptyMessage?: string;
    onResult?: (result: TResult) => void;
    onMessagesChange?: (messages: ContextualAiMessage[]) => void;
    onContextSummaryChange?: (summary: string) => void;
    renderResult?: (result: TResult) => ReactNode;
    footer?: ReactNode;
    disabled?: boolean;
    voiceDisabled?: boolean;
    testId?: string;
    transcriptTestId?: string;
    composerTestId?: string;
    composerContentTestId?: string;
    sendAriaLabel?: string;
}

const messageKey = (message: ContextualAiMessage, index: number) => `${message.role}-${index}-${message.content.slice(0, 24)}`;

const strengthsFirstWorkflows = new Set([
    'template-generation',
    'template-editor',
    'onboarding-success-profile',
    'ongoing-assessment',
]);

const ContextualAiWorkspace = <TResult,>({
    adapter,
    ariaLabel = 'Synergist AI workspace',
    composerLabel = 'Message Synergist AI',
    emptyMessage = 'Tell Synergist AI what you want to accomplish, or choose one of the suggestions below.',
    onResult,
    onMessagesChange,
    onContextSummaryChange,
    renderResult,
    footer,
    disabled = false,
    voiceDisabled = false,
    testId = 'contextual-ai-workspace',
    transcriptTestId,
    composerTestId,
    composerContentTestId,
    sendAriaLabel = 'Send message',
}: ContextualAiWorkspaceProps<TResult>) => {
    const [messages, setMessages] = useState<ContextualAiMessage[]>(adapter.initialMessages ?? []);
    const [composer, setComposer] = useState('');
    const [summary, setSummary] = useState(adapter.initialContextSummary ?? '');
    const [summaryDraft, setSummaryDraft] = useState(adapter.initialContextSummary ?? '');
    const [editingSummary, setEditingSummary] = useState(false);
    const [question, setQuestion] = useState(adapter.initialQuestion ?? '');
    const [choices, setChoices] = useState(adapter.initialChoices ?? []);
    const [canSkip, setCanSkip] = useState(Boolean(adapter.capabilities.skip));
    const [canGenerateNow, setCanGenerateNow] = useState(Boolean(adapter.capabilities.generateNow));
    const [allowFreeText, setAllowFreeText] = useState(adapter.capabilities.freeText !== false);
    const [progressLabel, setProgressLabel] = useState(adapter.progressLabel ?? '');
    const [result, setResult] = useState<TResult | null>(null);
    const [coverageDecision, setCoverageDecision] = useState<ContextualAiCoverageDecision | null>(adapter.initialCoverageDecision ?? null);
    const [coverageSummary, setCoverageSummary] = useState<ContextualAiCoverageSummary | null>(adapter.initialCoverageSummary ?? null);
    const [describingCoverage, setDescribingCoverage] = useState(false);
    const [busy, setBusy] = useState(false);
    const [turnTakingLong, setTurnTakingLong] = useState(false);
    const [error, setError] = useState('');
    const [lastAttempt, setLastAttempt] = useState<{ action: ContextualAiAction; text?: string; source: ContextualAiMessage['source']; metadata?: Record<string, unknown> } | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const transcriptRef = useRef<HTMLDivElement | null>(null);
    const endRef = useRef<HTMLDivElement | null>(null);
    const inFlightRef = useRef(false);
    const autoScrollPendingRef = useRef(false);
    const viewport = useViewportMode();

    const sessionSeedRef = useRef({
        sessionKey: adapter.sessionKey,
        initialMessages: adapter.initialMessages,
        initialContextSummary: adapter.initialContextSummary,
        initialQuestion: adapter.initialQuestion,
        initialChoices: adapter.initialChoices,
        initialCoverageDecision: adapter.initialCoverageDecision,
        initialCoverageSummary: adapter.initialCoverageSummary,
        progressLabel: adapter.progressLabel,
        capabilities: adapter.capabilities,
    });
    if (sessionSeedRef.current.sessionKey !== adapter.sessionKey) {
        sessionSeedRef.current = {
            sessionKey: adapter.sessionKey,
            initialMessages: adapter.initialMessages,
            initialContextSummary: adapter.initialContextSummary,
            initialQuestion: adapter.initialQuestion,
            initialChoices: adapter.initialChoices,
            initialCoverageDecision: adapter.initialCoverageDecision,
            initialCoverageSummary: adapter.initialCoverageSummary,
            progressLabel: adapter.progressLabel,
            capabilities: adapter.capabilities,
        };
    }

    useEffect(() => {
        const seed = sessionSeedRef.current;
        autoScrollPendingRef.current = false;
        if (transcriptRef.current)
            transcriptRef.current.scrollTop = 0;
        setMessages(seed.initialMessages ?? []);
        setComposer('');
        setSummary(seed.initialContextSummary ?? '');
        setSummaryDraft(seed.initialContextSummary ?? '');
        setQuestion(seed.initialQuestion ?? '');
        setChoices(seed.initialChoices ?? []);
        setCanSkip(Boolean(seed.capabilities.skip));
        setCanGenerateNow(Boolean(seed.capabilities.generateNow));
        setAllowFreeText(seed.capabilities.freeText !== false);
        setProgressLabel(seed.progressLabel ?? '');
        setResult(null);
        setCoverageDecision(seed.initialCoverageDecision ?? null);
        setCoverageSummary(seed.initialCoverageSummary ?? null);
        setDescribingCoverage(false);
        setError('');
        setLastAttempt(null);
    }, [adapter.sessionKey]);

    const externalCoverageFingerprint = adapter.initialCoverageDecision?.coverageFingerprint ?? '';
    useEffect(() => {
        if (!externalCoverageFingerprint)
            return;
        setCoverageDecision(current => current?.coverageFingerprint === externalCoverageFingerprint ? current : adapter.initialCoverageDecision ?? null);
    }, [adapter.initialCoverageDecision, externalCoverageFingerprint]);

    const externalCoverageSummaryKey = adapter.initialCoverageSummary?.categories.map(item => item.coverageFingerprint).join('::') ?? '';
    useEffect(() => {
        if (!externalCoverageSummaryKey)
            return;
        setCoverageSummary(current => current?.categories.map(item => item.coverageFingerprint).join('::') === externalCoverageSummaryKey ? current : adapter.initialCoverageSummary ?? null);
    }, [adapter.initialCoverageSummary, externalCoverageSummaryKey]);

    useEffect(() => {
        if (!autoScrollPendingRef.current)
            return;
        autoScrollPendingRef.current = false;
        endRef.current?.scrollIntoView?.({ block: 'nearest' });
    }, [messages, busy, question]);
    useEffect(() => onMessagesChange?.(messages), [messages, onMessagesChange]);

    useEffect(() => {
        if (!busy) {
            setTurnTakingLong(false);
            return;
        }
        const timer = window.setTimeout(() => setTurnTakingLong(true), 10000);
        return () => window.clearTimeout(timer);
    }, [busy]);

    const applyResponse = useCallback((response: ContextualAiTurnResult<TResult>, nextMessages: ContextualAiMessage[]) => {
        autoScrollPendingRef.current = true;
        const withAssistant = response.assistantMessage
            ? [...nextMessages, { role: 'assistant' as const, content: response.assistantMessage, source: 'system' as const }]
            : nextMessages;
        setMessages(withAssistant);
        if (response.contextSummary !== undefined) {
            setSummary(response.contextSummary);
            setSummaryDraft(response.contextSummary);
            onContextSummaryChange?.(response.contextSummary);
        }
        setQuestion(response.question ?? '');
        setChoices(response.choices ?? []);
        setCanSkip(response.canSkip ?? Boolean(adapter.capabilities.skip));
        setCanGenerateNow(response.canGenerateNow ?? Boolean(adapter.capabilities.generateNow));
        setAllowFreeText(response.allowFreeText ?? adapter.capabilities.freeText !== false);
        setProgressLabel(response.progressLabel ?? '');
        if (response.result !== undefined) {
            setResult(response.result);
            onResult?.(response.result);
        }
        if (response.needsCoverageDecision !== undefined) {
            setCoverageDecision(response.needsCoverageDecision);
            if (response.needsCoverageDecision)
                setCoverageSummary(null);
            setDescribingCoverage(false);
        }
        if (response.coverageSummary !== undefined) {
            setCoverageSummary(response.coverageSummary);
            if (response.coverageSummary)
                setCoverageDecision(null);
        }
    }, [adapter.capabilities.freeText, adapter.capabilities.generateNow, adapter.capabilities.skip, onContextSummaryChange, onResult]);

    const submit = useCallback(async (action: ContextualAiAction, text?: string, source: ContextualAiMessage['source'] = 'text', metadata?: Record<string, unknown>, replay = false) => {
        const normalized = text?.trim();
        if (inFlightRef.current || disabled || (action === 'answer' && !normalized))
            return null;
        inFlightRef.current = true;
        autoScrollPendingRef.current = true;
        setBusy(true);
        setError('');
        setLastAttempt({ action, text: normalized, source, metadata });
        const nextMessages = action === 'answer' && normalized && !replay
            ? [...messages, { role: 'user' as const, content: normalized, source }]
            : messages;
        if (nextMessages !== messages)
            setMessages(nextMessages);
        setComposer('');
        try {
            const response = await adapter.submit({
                action,
                text: normalized,
                messages: nextMessages,
                contextSummary: summary,
                metadata: adapter.contextMetadata || metadata
                    ? { ...adapter.contextMetadata, ...metadata }
                    : undefined,
            });
            applyResponse(response, nextMessages);
            return response.assistantMessage ?? null;
        } catch {
            if ((action === 'answer' || action === 'coverage-describe-missing') && normalized)
                setComposer(normalized);
            setError(adapter.failureMessage?.({ action, text: normalized, source, metadata }) ?? (source === 'voice'
                ? 'Synergist AI could not complete that turn. Your spoken request is preserved and ready to retry.'
                : 'Synergist AI could not complete that turn. Your conversation and draft are safe.'));
            return null;
        } finally {
            inFlightRef.current = false;
            setBusy(false);
        }
    }, [adapter, applyResponse, disabled, messages, summary]);

    const voice = useLiveAiVoice({
        workflow: adapter.voiceWorkflow ?? 'TemplateCreation',
        contextId: adapter.contextId,
        disabled: disabled || voiceDisabled || busy || adapter.capabilities.voice === false,
        onUserTranscript: (transcript, voiceSessionId) => submit('answer', transcript, 'voice', { voiceSessionId }),
        onFallbackToText: () => inputRef.current?.focus(),
    });

    const summaryChanged = summaryDraft.trim() !== summary.trim();
    const renderedResult = useMemo(() => result !== null && renderResult ? renderResult(result) : null, [renderResult, result]);

    return (
        <Paper
            variant="outlined"
            aria-label={ariaLabel}
            aria-busy={busy}
            data-testid={testId}
            data-ai-workflow={adapter.workflow}
            sx={{ height: '100%', maxHeight: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        >
            <Box sx={{ p: 1.5, flex: '0 0 auto' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <AutoAwesomeRounded color="primary" />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography fontWeight={800}>Synergist AI</Typography>
                        <Typography variant="caption" color="text.secondary">Context-aware help for this task</Typography>
                    </Box>
                    {progressLabel && <Chip size="small" variant="outlined" label={progressLabel} />}
                </Stack>
                {adapter.capabilities.editableContextSummary !== false && summary && (
                    <Box sx={{ mt: 1 }}>
                        {editingSummary ? (
                            <Stack spacing={1}>
                                <TextField
                                    multiline
                                    minRows={2}
                                    size="small"
                                    label="What Synergist AI understands"
                                    value={summaryDraft}
                                    onChange={event => setSummaryDraft(event.target.value)}
                                />
                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                    <Button size="small" color="inherit" onClick={() => { setSummaryDraft(summary); setEditingSummary(false); }}>Cancel</Button>
                                    <Button size="small" variant="outlined" disabled={!summaryChanged || !summaryDraft.trim()} onClick={() => { const next = summaryDraft.trim(); setSummary(next); onContextSummaryChange?.(next); setEditingSummary(false); }}>Confirm context</Button>
                                </Stack>
                            </Stack>
                        ) : (
                            <Alert severity="info" action={<Button size="small" onClick={() => setEditingSummary(true)}>Edit</Button>}>
                                <Typography variant="caption" fontWeight={700}>Confirmed context</Typography>
                                <Typography variant="body2">{summary}</Typography>
                            </Alert>
                        )}
                    </Box>
                )}
            </Box>
            <Divider />
            <Stack ref={transcriptRef} spacing={1.25} sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', p: 1.5, scrollPaddingBottom: 128 }} data-testid={transcriptTestId ?? `${testId}-transcript`}>
                {strengthsFirstWorkflows.has(adapter.workflow) && (
                    <Alert severity="info" data-testid="contextual-ai-strengths-first-guidance">
                        <strong>Strengths first.</strong> Recognize what the person does well and the evidence that supports it before discussing growth. Frame gaps as observable, role-specific development opportunities that can help the person improve in the role—not personality or moral judgments. Use counterproductive behaviors sparingly, only when they are observable and consequential.
                    </Alert>
                )}
                {messages.length === 0 && !question && <Typography variant="body2" color="text.secondary">{emptyMessage}</Typography>}
                {messages.map((message, index) => (
                    <Box key={messageKey(message, index)} sx={{ alignSelf: message.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '90%' }}>
                        <Typography variant="caption" color="text.secondary">{message.role === 'user' ? message.source === 'voice' ? 'You · Voice' : 'You' : 'Synergist AI'}</Typography>
                        <Box sx={{ mt: 0.25, p: 1.15, borderRadius: message.role === 'user' ? '14px 4px 14px 14px' : '4px 14px 14px 14px', bgcolor: message.role === 'user' ? 'primary.main' : 'action.hover', color: message.role === 'user' ? 'primary.contrastText' : 'text.primary' }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{message.content}</Typography>
                        </Box>
                    </Box>
                ))}
                {question && (
                    <Box>
                        <Typography variant="subtitle2">{question}</Typography>
                        {choices.length > 0 && (
                            <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.75, mt: 1 }} aria-label="Suggested answers">
                                {choices.map(choice => <Chip key={choice.id} clickable disabled={busy || disabled} label={choice.label} onClick={() => void submit('answer', choice.value ?? choice.label, 'choice')} />)}
                            </Stack>
                        )}
                    </Box>
                )}
                {coverageDecision && (
                    <Alert severity="info" data-testid="contextual-ai-coverage-decision" icon={<AutoAwesomeRounded />}>
                        <Stack spacing={1}>
                            <Box>
                                <Typography fontWeight={800}>Complete {coverageDecision.category}</Typography>
                                <Typography variant="body2">
                                    This category has {coverageDecision.includedCount} distinct {coverageDecision.includedCount === 1 ? 'criterion' : 'criteria'}.
                                    {' '}{coverageDecision.coverageRationale || 'Does this fully cover success and meaningful risk for this role?'}
                                </Typography>
                            </Box>
                            {coverageDecision.suggestions.length > 0 && (
                                <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.75 }} aria-label={`Suggestions for ${coverageDecision.category}`}>
                                    {coverageDecision.suggestions.map(suggestion => <Chip key={suggestion.id} label={suggestion.label} />)}
                                </Stack>
                            )}
                            {describingCoverage && (
                                <TextField
                                    autoFocus
                                    size="small"
                                    label={`What is missing from ${coverageDecision.category}?`}
                                    value={composer}
                                    onChange={event => setComposer(event.target.value)}
                                    onKeyDown={event => {
                                        if (event.key === 'Enter' && !event.shiftKey && composer.trim()) {
                                            event.preventDefault();
                                            void submit('coverage-describe-missing', composer, 'text', {
                                                category: coverageDecision.category,
                                                coverageFingerprint: coverageDecision.coverageFingerprint,
                                                coverageContractVersion: coverageDecision.coverageContractVersion,
                                            });
                                        }
                                    }}
                                />
                            )}
                            <Stack direction={{ xs: 'column', sm: 'row' }} flexWrap="wrap" sx={{ gap: 0.75 }}>
                                {coverageDecision.availableActions.includes('ConfirmSufficient') && <Button size="small" color="inherit" onClick={() => void submit('coverage-confirm-sufficient', undefined, 'choice', { category: coverageDecision.category, includedCount: coverageDecision.includedCount, coverageFingerprint: coverageDecision.coverageFingerprint, coverageContractVersion: coverageDecision.coverageContractVersion, sufficiencyConfirmed: true })}>Looks sufficient</Button>}
                                {coverageDecision.availableActions.includes('AddSuggestedItems') && <Button size="small" variant="contained" disabled={busy || coverageDecision.suggestions.length === 0} onClick={() => void submit('coverage-add-suggestions', undefined, 'choice', { category: coverageDecision.category, suggestions: coverageDecision.suggestions, coverageFingerprint: coverageDecision.coverageFingerprint, coverageContractVersion: coverageDecision.coverageContractVersion })}>Add suggestions</Button>}
                                {coverageDecision.availableActions.includes('DescribeMissingCoverage') && <Button size="small" onClick={() => setDescribingCoverage(true)}>Tell Synergist AI what is missing</Button>}
                                {coverageDecision.availableActions.includes('RegenerateCategory') && <Button size="small" onClick={() => void submit('coverage-regenerate', undefined, 'choice', { category: coverageDecision.category, coverageFingerprint: coverageDecision.coverageFingerprint, coverageContractVersion: coverageDecision.coverageContractVersion })}>Explore further</Button>}
                            </Stack>
                            <Typography variant="caption" color="text.secondary">Your coverage confirmation is saved with the conversation and is revisited if later denials create a gap.</Typography>
                        </Stack>
                    </Alert>
                )}
                {!coverageDecision && coverageSummary && (
                    <Alert severity="info" data-testid="contextual-ai-coverage-summary">
                        <Stack spacing={1}>
                            <Typography fontWeight={800}>Review role coverage</Typography>
                            <Typography variant="body2">{coverageSummary.contextSummary}</Typography>
                            <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.5 }}>
                                {coverageSummary.categories.map(category => (
                                    <Typography component="li" variant="body2" key={category.category}>
                                        <strong>{category.category}:</strong> {category.includedCount} distinct criteria. {category.coverageRationale}
                                    </Typography>
                                ))}
                            </Stack>
                            {coverageSummary.categories.every(category =>
                                category.availableActions.includes('ConfirmSufficient')
                                && category.coverageContractVersion === coverageSummary.categories[0]?.coverageContractVersion
                            ) && <Button
                                variant="contained"
                                disabled={busy}
                                onClick={() => void submit('coverage-confirm-all-sufficient', undefined, 'choice', {
                                    confirmations: coverageSummary.categories.map(category => ({ category: category.category, coverageFingerprint: category.coverageFingerprint, coverageContractVersion: category.coverageContractVersion })),
                                    coverageContractVersion: coverageSummary.categories[0]?.coverageContractVersion,
                                    contextSummary: coverageSummary.contextSummary,
                                })}
                            >
                                This template sufficiently covers the role
                            </Button>}
                        </Stack>
                    </Alert>
                )}
                {renderedResult}
                {busy && <Alert severity="info" icon={<CircularProgress size={18} />}>
                    {turnTakingLong
                        ? 'Synergist AI is still working. Your answer and conversation are safe if this turn needs to be retried.'
                        : lastAttempt?.action === 'generate-now'
                            ? 'Generating a valid result from what we know…'
                            : lastAttempt?.action === 'answer'
                                ? 'Saving your answer and preparing the next question…'
                                : progressLabel || 'Synergist AI is working…'}
                </Alert>}
                {error && (
                    <Alert severity="warning" action={lastAttempt ? <Button color="inherit" size="small" onClick={() => void submit(lastAttempt.action, lastAttempt.text, lastAttempt.source, lastAttempt.metadata, true)}>Retry</Button> : undefined}>
                        {error}
                    </Alert>
                )}
                <div ref={endRef} />
            </Stack>
            <Divider />
            <Stack spacing={1} sx={{ p: 1.5, pb: 'calc(12px + env(safe-area-inset-bottom, 0px))', flex: '0 0 auto', bgcolor: 'background.paper', position: 'sticky', bottom: 0 }} data-testid={composerTestId ?? `${testId}-composer`}>
                {allowFreeText && (
                    <Stack
                        data-testid={composerContentTestId}
                        data-layout={viewport.isPhone ? 'phone' : viewport.isTablet ? 'tablet' : 'desktop'}
                        direction={viewport.isPhone ? 'column' : 'row'}
                        spacing={1}
                        alignItems={viewport.isPhone ? 'stretch' : 'flex-end'}
                    >
                        <Stack spacing={0.5} sx={{ flex: '1 1 auto', minWidth: 0 }}>
                            <Typography
                                component="label"
                                variant="caption"
                                color="text.secondary"
                                data-testid={`${testId}-composer-label`}
                            >
                                {composerLabel}
                            </Typography>
                            <TextField
                                inputRef={inputRef}
                                fullWidth
                                multiline
                                maxRows={4}
                                size="small"
                                inputProps={{ 'aria-label': composerLabel }}
                                value={composer}
                                onChange={event => setComposer(event.target.value)}
                                onKeyDown={event => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); void submit('answer', composer); } }}
                            />
                        </Stack>
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button aria-label={sendAriaLabel} variant="contained" disabled={busy || disabled || !composer.trim()} onClick={() => void submit('answer', composer)} sx={{ minHeight: 40, minWidth: 48 }}><SendRounded fontSize="small" /></Button>
                            {adapter.capabilities.voice !== false && <LiveAiVoiceControls voice={voice} disabled={busy || disabled || voiceDisabled} />}
                        </Stack>
                    </Stack>
                )}
                {(canSkip || canGenerateNow || footer) && (
                    <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="flex-end">
                        {footer}
                        {canSkip && <Button color="inherit" disabled={busy || disabled} onClick={() => void submit('skip')}>Skip</Button>}
                        {canGenerateNow && <Button variant="outlined" disabled={busy || disabled} onClick={() => void submit('generate-now')}>Generate now</Button>}
                    </Stack>
                )}
            </Stack>
        </Paper>
    );
};

export default ContextualAiWorkspace;
